import type { NodeViewProps } from "@tiptap/react";
import { NodeViewWrapper } from "@tiptap/react";
import { File, Loader2 } from "lucide-react";
import { useMemo } from "react";
import { cfResizedUrl } from "@/lib/image-urls";
import { cn } from "@/lib/utils";
import type { OrsaneMediaNodeType } from "./OrsaneMediaNode";

function OtherMedia({
    fileName,
    url,
    href,
    isLoading,
}: {
    fileName: string;
    url: string;
    href: string | undefined;
    isLoading?: boolean;
}) {
    const finalHref = href || url;
    return (
        <a
            href={finalHref}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(isLoading && "pointer-events-none")}
        >
            {isLoading ? (
                <Loader2 className="w-4 text-blue-900/60 animate-spin" />
            ) : (
                <File className="w-4 text-blue-900/60" />
            )}
            {fileName}
        </a>
    );
}

function ImageMedia({
    url,
    fileName,
    href,
    isLoading,
}: {
    url: string;
    fileName: string;
    href: string | undefined;
    isLoading?: boolean;
}) {
    const finalHref = href || url;
    return (
        <a
            href={finalHref}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(isLoading && "loading")}
            onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                const event = new CustomEvent("orsane-image-click", {
                    bubbles: true,
                    detail: { src: finalHref },
                });
                e.currentTarget.dispatchEvent(event);
            }}
        >
            <img src={url} alt={fileName} />
            {/* below is only used in initial load when uploading */}
            {isLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/20 text-white  font-medium">
                    <Loader2 className="w-4 animate-spin" />
                </div>
            )}
        </a>
    );
}

export default function OrsaneMediaComponent({ node }: NodeViewProps) {
    const { fileName, url, contentType, href } = node.attrs as OrsaneMediaNodeType;
    const isLoading = url.startsWith("blob:"); // Temporary URLs are blob URLs
    const isImage = contentType.startsWith("image/");

    const finalUrl = useMemo(() => {
        if (isImage && !isLoading) {
            return cfResizedUrl(url, 400, 400);
        }
        return url;
    }, [url, isImage, isLoading]);

    return (
        <>
            {isImage && (
                <NodeViewWrapper data-type="orsanemedia" className="orsanemedia--image">
                    <ImageMedia fileName={fileName} url={finalUrl} href={href} isLoading={isLoading} />
                </NodeViewWrapper>
            )}
            {!isImage && (
                <NodeViewWrapper data-type="orsanemedia" className="orsanemedia--other">
                    <OtherMedia fileName={fileName} url={url} href={href} isLoading={isLoading} />
                </NodeViewWrapper>
            )}
        </>
    );
}
